import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { LuArrowLeft, LuArrowLeftRight, LuBookOpen, LuFlaskConical, LuPercent, LuShieldCheck } from 'react-icons/lu';
import { apiCall } from "../utils/api";
import { cn } from "../utils/cn";
import logo from "../assets/logo.png";
import { gsap, useGSAP, prefersReducedMotion, CLEAR_PROPS } from "../lib/gsap";
import Button from "../components/ui/button";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;
const emptyOtp = () => Array(OTP_LENGTH).fill("");

// Seconds left before another OTP may be sent to `number` (0 = may send now).
// `last` is { number, at } for the most recent successful send.
const cooldownLeft = (last, number) => {
  if (!last || last.number !== number) return 0;
  return Math.max(0, RESEND_SECONDS - Math.floor((Date.now() - last.at) / 1000));
};

// Development-only test aid: when the login API includes the OTP in its response, show it on
// the OTP screen so testers don't need the account owner's SMS. `npm start` sets NODE_ENV to
// 'development'; production builds set 'production', and this panel is removed from them.
const SHOW_DEV_OTP = process.env.NODE_ENV === 'development';
const DEV_OTP_FIELDS = ['otp', 'dev_otp', 'test_otp', 'otp_code'];
const pickDevOtp = (data) => {
  for (const field of DEV_OTP_FIELDS) {
    const digits = String(data?.[field] ?? '').replace(/\D/g, '');
    if (digits.length === OTP_LENGTH) return digits;
  }
  return '';
};

const FEATURES = [
  { icon: LuArrowLeftRight, title: 'Live transactions', text: 'Recharges, bill payments and wallet top-ups as they happen.' },
  { icon: LuPercent, title: 'Margins & providers', text: 'Commission plans and API providers in one place.' },
  { icon: LuBookOpen, title: 'Khatabook', text: 'Banks, parties and payments, always reconciled.' },
];

const Logo = ({ className }) => (
  <img src={logo} alt="" className={cn('h-11 w-11 rounded-xl shadow-lg shadow-brand-600/30', className)} />
);

function Login() {
  const navigate = useNavigate();

  // Login states
  const [mobile, setMobile] = useState("");
  const [error, setError] = useState("");
  const [loading, setIsLoading] = useState(false);

  // OTP states
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(emptyOtp);
  const [timer, setTimer] = useState(RESEND_SECONDS);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpToken, setOtpToken] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const inputRefs = useRef([]);
  // One send request at a time: state updates too late to stop a fast double-tap or double Enter
  const sendingRef = useRef(false);
  // Most recent successful send { number, at, devOtp }: going back to "Change number" and
  // pressing Send OTP again must not bypass the resend wait or send a second SMS
  const lastSentRef = useRef(null);
  const pageRef = useRef(null);
  const stepRef = useRef(null);

  const canResend = timer === 0;
  const maskedMobile = mobile
    ? mobile.slice(0, 2) + "****" + mobile.slice(6)
    : "XXXXXXXXXX";

  // Entrance: brand panel content, then the form
  useGSAP(() => {
    if (prefersReducedMotion()) return;
    gsap.timeline({ defaults: { ease: 'power3.out', clearProps: CLEAR_PROPS } })
      .from('[data-intro]', { opacity: 0, y: 20, duration: 0.6, stagger: 0.08 })
      .from('[data-form]', { opacity: 0, y: 16, duration: 0.5 }, 0.15);
  }, { scope: pageRef });

  // Slide between the mobile-number step and the OTP step
  useGSAP(() => {
    if (prefersReducedMotion()) return;
    gsap.from(stepRef.current, { opacity: 0, x: showOtp ? 24 : -24, duration: 0.35, clearProps: CLEAR_PROPS });
  }, { dependencies: [showOtp], scope: stepRef });

  useEffect(() => {
    document.title = 'Sign in';
  }, []);

  // Resend countdown, computed from the send time so it stays right after going back and forth
  useEffect(() => {
    if (!showOtp) return undefined;
    setTimer(cooldownLeft(lastSentRef.current, mobile));
    const id = setInterval(() => setTimer(cooldownLeft(lastSentRef.current, mobile)), 1000);
    return () => clearInterval(id);
  }, [showOtp, mobile]);

  // Focus first OTP input when shown
  useEffect(() => {
    if (showOtp) inputRefs.current[0]?.focus();
  }, [showOtp]);

  const handleMobileChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobile(val);
    if (error) setError("");
  };

  const handleMobileSubmit = async (e) => {
    e.preventDefault();

    if (mobile.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    if (sendingRef.current) return;

    // Same number still inside the resend wait: reuse the OTP already sent (a new one would
    // cancel it, so the code in the SMS would stop working)
    const wait = cooldownLeft(lastSentRef.current, mobile);
    if (wait > 0) {
      if (SHOW_DEV_OTP) setDevOtp(lastSentRef.current.devOtp);
      setShowOtp(true);
      toast(`OTP already sent. You can request a new one in ${wait}s.`);
      return;
    }

    sendingRef.current = true;
    setIsLoading(true);

    try {
      const result = await apiCall("login", { number: mobile }, false);
      if (result.status === true) {
        const code = SHOW_DEV_OTP ? pickDevOtp(result.data) : "";
        lastSentRef.current = { number: mobile, at: Date.now(), devOtp: code };
        setOtpToken(result.data.otp_token);
        setDevOtp(code);
        setShowOtp(true);
        setTimer(RESEND_SECONDS);
        toast.success("OTP sent successfully!");
      } else if (result.response_code === 3) {
        // User not registered - show toast and stay on login
        toast.error("Mobile number not registered. Please contact support.");
      } else {
        toast.error(result.message || "An error occurred. Please try again.");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      sendingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleVerify = useCallback(async () => {
    const code = otp.join("");
    if (code.length !== OTP_LENGTH) {
      toast.error("Please enter complete 6-digit OTP");
      return;
    }

    setVerifying(true);

    try {
      const result = await apiCall(
        "verify-otp",
        {
          number: mobile,
          otp: code,
          otp_token: otpToken
        },
        false
      );

      if (result.status === true) {
        toast.success("OTP verified successfully!");
        localStorage.setItem("credentials", JSON.stringify(result.data));
        navigate("/", { replace: true });
      } else {
        toast.error(result.message || "Invalid OTP. Please try again.");
        setOtp(emptyOtp());
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
      setOtp(emptyOtp());
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  }, [otp, mobile, otpToken, navigate]);

  // Auto-verify OTP when complete
  useEffect(() => {
    const code = otp.join("");
    if (code.length === OTP_LENGTH && !verifying && showOtp) {
      const timeout = setTimeout(() => {
        handleVerify();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [otp, handleVerify, verifying, showOtp]);

  // Spread a pasted / autofilled code across the boxes
  const fillOtp = (text) => {
    const digits = text.replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!digits) return;
    setOtp(Array.from({ length: OTP_LENGTH }, (_, i) => digits[i] || ""));
    inputRefs.current[Math.min(digits.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    if (verifying) return;
    // SMS autofill can drop the whole code into the first box
    if (value.length > 1) {
      fillOtp(value);
      return;
    }
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    if (verifying) return;
    e.preventDefault();
    fillOtp(e.clipboardData.getData("text"));
  };

  const handleResend = async () => {
    if (!canResend || verifying || resending || sendingRef.current) return;

    sendingRef.current = true;
    setResending(true);

    try {
      const result = await apiCall(
        "login",
        { number: mobile },
        false
      );

      if (result.status === true) {
        const code = SHOW_DEV_OTP ? pickDevOtp(result.data) : "";
        lastSentRef.current = { number: mobile, at: Date.now(), devOtp: code };
        setOtpToken(result.data.otp_token);
        setDevOtp(code);
        toast.success("OTP resent successfully!");
        setTimer(RESEND_SECONDS);
        setOtp(emptyOtp());
        inputRefs.current[0]?.focus();
      } else {
        toast.error(result.message || "Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      sendingRef.current = false;
      setResending(false);
    }
  };

  const handleBack = () => {
    if (!verifying && !loading) {
      setShowOtp(false);
      setOtp(emptyOtp());
      setDevOtp("");
      setError("");
    }
  };

  return (
    <div ref={pageRef} className="flex min-h-screen bg-white dark:bg-slate-950">
      {/* Brand panel (desktop) */}
      <aside className="relative hidden w-[44%] max-w-xl flex-col justify-between overflow-hidden bg-gradient-to-br from-brand-600 via-brand-700 to-slate-900 p-12 dark:from-brand-700 dark:via-brand-800 dark:to-slate-950 text-white lg:flex">
        {/* soft light blobs, static so they cost nothing */}
        <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-brand-300/25 blur-3xl" />

        <div data-intro className="relative">
          <Logo className="h-12 w-12 shadow-xl shadow-black/20 ring-1 ring-white/30" />
        </div>

        <div className="relative">
          <h2 data-intro className="max-w-sm text-3xl font-semibold leading-tight tracking-tight">
            Run your recharge business from one place.
          </h2>
          <ul className="mt-10 space-y-6">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <li data-intro key={title} className="flex gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/20">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-medium">{title}</p>
                  <p className="mt-0.5 text-sm text-brand-100">{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p data-intro className="relative flex items-center gap-2 text-sm text-brand-100">
          <LuShieldCheck className="h-4 w-4" aria-hidden="true" />
          Requests are encrypted before they leave your browser
        </p>
      </aside>

      {/* Form */}
      <main className="flex flex-1 items-center justify-center bg-slate-50 px-4 py-10 sm:px-6 lg:bg-white dark:bg-slate-950">
        <div data-form className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <Logo />
          </div>

          <div ref={stepRef} key={showOtp ? 'otp' : 'mobile'} className="rounded-2xl bg-white p-6 shadow-card ring-1 ring-slate-200/80 sm:p-8 lg:p-0 lg:shadow-none lg:ring-0 dark:bg-slate-900 dark:ring-slate-800 dark:lg:bg-transparent">
            {!showOtp ? (
              <>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">Sign in</h1>
                <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">Enter your registered mobile number to receive a one-time password.</p>

                <form onSubmit={handleMobileSubmit} className="mt-8" noValidate>
                  <label htmlFor="mobile" className="block text-sm font-medium text-slate-700 dark:text-slate-300">Mobile number</label>
                  <div
                    className={cn(
                      'mt-2 flex h-12 items-center overflow-hidden rounded-lg bg-white ring-1 ring-inset transition-shadow focus-within:ring-2 dark:bg-slate-900',
                      error ? 'ring-rose-500 focus-within:ring-rose-500' : 'ring-slate-300 focus-within:ring-brand-500 dark:ring-slate-700'
                    )}
                  >
                    <span className="flex h-full items-center border-r border-slate-200 bg-slate-50 px-3.5 text-sm font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">+91</span>
                    <input
                      id="mobile"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      maxLength={10}
                      placeholder="10-digit mobile number"
                      value={mobile}
                      onChange={handleMobileChange}
                      disabled={loading}
                      aria-invalid={Boolean(error)}
                      aria-describedby={error ? 'mobile-error' : undefined}
                      className="h-full min-w-0 flex-1 bg-transparent px-3.5 text-[15px] tracking-wide text-slate-900 outline-none placeholder:tracking-normal placeholder:text-slate-400 disabled:opacity-60 dark:text-white dark:placeholder:text-slate-500"
                    />
                  </div>
                  {error && <p id="mobile-error" className="mt-2 text-sm text-rose-600 dark:text-rose-400">{error}</p>}

                  <Button type="submit" size="lg" loading={loading} className="mt-6 w-full">
                    {loading ? 'Sending OTP…' : 'Send OTP'}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={verifying || loading}
                  className="-ml-2 mb-4 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                >
                  <LuArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Change number
                </button>

                <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">Verify OTP</h1>
                <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
                  Enter the 6-digit code sent to <span className="font-medium text-slate-700 dark:text-slate-200">+91 {maskedMobile}</span>
                </p>

                <div className="mt-8 grid grid-cols-6 gap-2 sm:gap-2.5" onPaste={handleOtpPaste}>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      aria-label={`Digit ${index + 1}`}
                      maxLength={index === 0 ? OTP_LENGTH : 1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onFocus={(e) => e.target.select()}
                      disabled={verifying}
                      className={cn(
                        'h-12 w-full rounded-lg border bg-white text-center text-xl font-semibold text-slate-900 outline-none transition sm:h-14 dark:bg-slate-900 dark:text-white',
                        'focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 disabled:opacity-60',
                        digit ? 'border-brand-400 dark:border-brand-500' : 'border-slate-300 dark:border-slate-700'
                      )}
                    />
                  ))}
                </div>

                {/* Shown only when the server includes the OTP (development builds only) */}
                {SHOW_DEV_OTP && devOtp && (
                  <div className="mt-5 flex items-center justify-between gap-3 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-3.5 py-3 dark:border-amber-500/40 dark:bg-amber-500/10">
                    <div>
                      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                        <LuFlaskConical className="h-3.5 w-3.5" aria-hidden="true" />
                        Test code
                      </p>
                      <span className="mt-1 block font-mono text-lg font-semibold tracking-[0.3em] text-amber-900 dark:text-amber-100">
                        {devOtp}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => fillOtp(devOtp)}
                      disabled={verifying}
                      className="rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-60"
                    >
                      Use code
                    </button>
                  </div>
                )}

                <p className="mt-5 text-sm text-slate-500 dark:text-slate-400">
                  {canResend ? (
                    <>
                      Didn't get it?{' '}
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={verifying || resending}
                        className="font-semibold text-brand-600 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-50 dark:text-brand-400 dark:hover:text-brand-300"
                      >
                        {resending ? "Sending…" : "Resend OTP"}
                      </button>
                    </>
                  ) : (
                    <>Resend OTP in <span className="font-semibold tabular-nums text-slate-700 dark:text-slate-200">{timer}s</span></>
                  )}
                </p>

                <Button
                  size="lg"
                  onClick={handleVerify}
                  loading={verifying}
                  disabled={otp.join("").length !== OTP_LENGTH}
                  className="mt-6 w-full"
                >
                  {verifying ? 'Verifying…' : 'Verify & continue'}
                </Button>
              </>
            )}
          </div>

          <p className="mt-8 text-center text-xs leading-relaxed text-slate-400 dark:text-slate-500">
            By continuing you agree to our Terms &amp; Privacy Policy.
          </p>
        </div>
      </main>
    </div>
  );
}

export default Login;
